export const projectColors = {
  jotverse: {
    id: 'jotverse',
    label: 'Jotverse',
    badge: 'bg-cyan-500/20 border-cyan-400/50 text-cyan-100',
    dot: 'bg-cyan-400',
    border: 'border-cyan-400/40',
    glow: 'shadow-[0_0_20px_rgba(34,211,238,0.15)]',
    accent: 'text-cyan-300'
  },
  lexon: {
    id: 'lexon',
    label: 'Lexon',
    badge: 'bg-amber-500/20 border-amber-400/50 text-amber-100',
    dot: 'bg-amber-400',
    border: 'border-amber-400/40',
    glow: 'shadow-[0_0_20px_rgba(251,191,36,0.15)]',
    accent: 'text-amber-300'
  },
  sophia: {
    id: 'sophia',
    label: 'Sophia',
    badge: 'bg-rose-500/20 border-rose-400/50 text-rose-100',
    dot: 'bg-rose-400',
    border: 'border-rose-400/40',
    glow: 'shadow-[0_0_20px_rgba(251,113,133,0.15)]',
    accent: 'text-rose-300'
  },
  terc: {
    id: 'terc',
    label: 'TERC',
    badge: 'bg-emerald-500/20 border-emerald-400/50 text-emerald-100',
    dot: 'bg-emerald-400',
    border: 'border-emerald-400/40',
    glow: 'shadow-[0_0_20px_rgba(52,211,153,0.15)]',
    accent: 'text-emerald-300'
  },
  bridgespace: {
    id: 'bridgespace',
    label: 'BridgeSpace',
    badge: 'bg-violet-500/20 border-violet-400/50 text-violet-100',
    dot: 'bg-violet-400',
    border: 'border-violet-400/40',
    glow: 'shadow-[0_0_20px_rgba(167,139,250,0.15)]',
    accent: 'text-violet-300'
  }
};

export const capabilities = [
  {
    id: 'memory',
    title: 'Memory & Long-Term Context',
    shortLabel: 'Memory',
    summary:
      'Durable session and learning context that survives refreshes, partial failures, and multi-session use — without turning every conversation into unbounded storage.',
    projectIds: ['jotverse', 'sophia', 'bridgespace']
  },
  {
    id: 'pipelines',
    title: 'Async & Session Pipelines',
    shortLabel: 'Pipelines',
    summary:
      'Long-running ingestion and post-session analysis: queues, callbacks, retries, and structured extraction that stay reliable when inputs are messy and jobs take time.',
    projectIds: ['jotverse', 'lexon', 'sophia', 'terc', 'bridgespace']
  },
  {
    id: 'privacy',
    title: 'Privacy, PII & Compliance',
    shortLabel: 'Privacy',
    summary:
      'Privacy as architecture: redaction, fail-closed filtering, minimized retention, consent gates, and audit-friendly designs for sensitive domains.',
    projectIds: ['sophia', 'terc', 'bridgespace']
  },
  {
    id: 'retrieval',
    title: 'Grounded Retrieval & Reasoning',
    shortLabel: 'Retrieval',
    summary:
      'RAG and GraphRAG systems that combine semantic retrieval with structured knowledge so outputs stay inspectable, grounded, and useful under real scrutiny.',
    projectIds: ['lexon', 'jotverse']
  },
  {
    id: 'realtime',
    title: 'Real-Time & Multimodal AI UX',
    shortLabel: 'Real-time UX',
    summary:
      'Streaming chat, video companions, and media-heavy flows designed to feel responsive even when the underlying AI work is complex or long-running.',
    projectIds: ['jotverse', 'sophia']
  },
  {
    id: 'roles',
    title: 'Roles, Consent & Multi-Actor Systems',
    shortLabel: 'Roles & Consent',
    summary:
      'Role-scoped access across clients, clinicians, parents, reviewers, and admins — with consent, approval gates, and clear boundaries around who sees what.',
    projectIds: ['bridgespace', 'terc', 'lexon']
  }
];

export const projects = [
  {
    id: 'jotverse',
    name: 'Jotverse',
    title: 'AI Tutor & Learning Platform',
    ownership: 'Solo / end-to-end',
    summary:
      'Built the full AI tutoring product solo — from adaptive chat and durable learning memory through billing and deploy — so context survives across sessions.',
    bullets: [
      'Custom AI tutor with multi-model workflows, streaming UX, and async learning analysis',
      'Durable memory and state across Redis, DynamoDB, and structured learning history',
      'Content ingestion from documents and video into production-ready learning material'
    ],
    tech: [
      'Laravel 11',
      'Vue 3',
      'Redis',
      'Pinecone',
      'OpenAI',
      'Gemini',
      'DynamoDB',
      'AWS',
      'Paddle'
    ],
    capabilityIds: ['memory', 'pipelines', 'retrieval', 'realtime']
  },
  {
    id: 'lexon',
    name: 'Lexon',
    title: 'AI Legal Reasoning Platform',
    ownership: 'Solo / architected & built',
    summary:
      'Independently designed a GraphRAG legal reasoning platform that turns unstructured material into a reviewable knowledge graph for grounded, explainable analysis.',
    bullets: [
      'GraphRAG architecture combining semantic retrieval, Neo4j relationships, and multi-step reasoning',
      'Human-in-the-loop review and publishing so draft outputs stay separate from trusted knowledge',
      'Integrity checks and governance tooling for a growing legal corpus'
    ],
    tech: [
      'Python',
      'FastAPI',
      'Next.js',
      'Neo4j',
      'PostgreSQL',
      'OpenAI',
      'Gemini',
      'CrewAI',
      'Redis'
    ],
    capabilityIds: ['pipelines', 'retrieval', 'roles']
  },
  {
    id: 'sophia',
    name: 'Sophia',
    title: 'AI Coaching & Session Intelligence',
    ownership: 'Core contributor',
    summary:
      'Helped architect and build an AI coaching platform for therapists and coaches — session analysis, privacy-aware processing, and long-running audio pipelines.',
    bullets: [
      'Structured session insights grounded in transcripts and longitudinal context',
      'GPU-backed transcription and diarisation with retry-safe job orchestration',
      'Privacy-aware anonymisation and multi-stage filtering for sensitive conversations'
    ],
    tech: [
      'React',
      'Vite',
      'Supabase',
      'Cloud Run',
      'Whisper',
      'Pyannote',
      'OpenAI',
      'Docker'
    ],
    capabilityIds: ['memory', 'pipelines', 'privacy', 'realtime']
  },
  {
    id: 'terc',
    name: 'TERC',
    title: 'Dispute Resolution Platform',
    ownership: 'Solo / end-to-end',
    summary:
      'Built a compliance-oriented dispute platform end-to-end, with AI case analysis, strict permissioning, and secure document workflows.',
    bullets: [
      'Async AI analysis that turns case submissions into structured, reviewable outputs',
      'Privacy-first handling with clear separation between submission, analysis, and review',
      'Role-scoped access and e-signature document flows for legally sensitive actions'
    ],
    tech: [
      'Laravel',
      'Vue 3',
      'OpenAI',
      'Redis',
      'AWS SQS',
      'Dropbox Sign',
      'MySQL',
      'Pusher'
    ],
    capabilityIds: ['pipelines', 'privacy', 'roles']
  },
  {
    id: 'bridgespace',
    name: 'BridgeSpace',
    title: 'AI Companion for Neurodivergent Children',
    ownership: 'Core contributor',
    summary:
      'Core contributor on BridgeSpace / HelloMyFriend. I built the memory service — anonymization, normalization, auditing, and the AI calls that create session memories — plus the multi-tenant auth for clinicians, parents, and children.',
    bullets: [
      'HelloMyFriend memory service: anonymization and redaction, normalization, audit events, and the AI calls that turn sessions into structured memory',
      'Integrated that service with the existing app, including endpoints so Tavus sessions (built by a teammate) receive the child-scoped context they need',
      'Multi-tenant auth: clinician access codes, and parent registration that links a parent to a clinician and registers their children at the same time'
    ],
    tech: [
      'React',
      'TypeScript',
      'tRPC',
      'FastAPI',
      'Tavus',
      'Presidio',
      'Postgres',
      'Better Auth',
      'OpenAI'
    ],
    capabilityIds: ['memory', 'pipelines', 'privacy', 'roles']
  }
];
